import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { UnleashService } from '@snowfrog/ngx-unleash-proxy-client'
import jwtDecode from 'jwt-decode'
import Keycloak from 'keycloak-js'
import * as cloneDeep from 'lodash/cloneDeep'
import { UserEntryService } from 'src/app/dashboard/components/user/pages/user-entry/user-entry.service'
import { SessionComponent } from 'src/app/shared/components/session-dialog/session-dialog.component'
//import { AnalyticsEvents } from 'src/app/shared/constants/jitsu-analytics-keys'
import { StorageKeys } from 'src/app/shared/constants/storage-keys'
import { environment } from 'src/environments/environment'

import { CoreModule } from '../../core.module'
import { ApiService } from '../http/api.service'
import { IdentityProvider } from '../identity/identity-provider.interface'
import { KeycloakIdentityProvider } from '../identity/keycloak-identity.provider'
import { JitsuService } from '../jitsu/jitsu.service'
import { LocalStorageService } from '../storage/local-storage.service'
import { SessionStorageService } from '../storage/session-storage.service'

@Injectable({
  providedIn: CoreModule
})
export class AuthService {
  /** Set by keycloak-init.factory after init. */
  identity!: IdentityProvider
  constructor(
    private api: ApiService,
    private service: UserEntryService,
    private dialog: MatDialog,
    private unleashService: UnleashService,
    private readonly jitsu: JitsuService,
    private router: Router,
    private readonly storage: LocalStorageService,
    private readonly sessionStorage: SessionStorageService
  ) {}

  // Set on user-initiated logout, never reset: onAuthLogout and the bridge would both undo it.
  public isSigningOut = false

  /** Legacy shim — undefined for non-Keycloak. Prefer `identity`. */
  get keycloak(): Keycloak | undefined {
    return this.identity instanceof KeycloakIdentityProvider ? this.identity.raw : undefined
  }

  get profile() {
    return this.identity?.getProfile()
  }
  get isLoggedIn() {
    if (this.identity?.isAuthenticated()) return true
    // Bridge-hydrated session: OIDC never ran, but the app-session token is live.
    return Boolean(this.storage.get(StorageKeys.authToken) && this.storage.get(StorageKeys.isUserAuthenticated))
  }
  refreshToken(cb: () => void, errCb: () => void) {
    this.identity
      .refreshToken(30)
      .then(() => cb())
      .catch(() => errCb())
  }

  public getUserInfo() {
    return new Promise((resolve, reject) => {
      const res = this.api.sendRequest({
        apiBase: `${environment.apiBase}/api/v2/`,
        method: 'get',
        endpoint: 'users/ui-info',
        queryParams: {
          source: 'ADMIN_UI'
        }
      })
      res.subscribe({
        next: data => {
          // handle the case if the user is inactive on km-app, but login via sso integration displays error info component
          if (data?.code) {
            resolve(data)
          } else {
            this.storage.set(StorageKeys.authToken, data.authToken)
            const decodedData: { isAdmin: boolean } = jwtDecode(data.authToken)
            if (environment.subdomain !== 'master') {
              const userInfo = cloneDeep(data)
              delete userInfo?.authToken
              this.jitsu.setUserInfoInJitsu(userInfo)
              // this.jitsu.track(AnalyticsEvents.userInformation, userInfo)
            }
            if ('isAdmin' in decodedData) data['isAdmin'] = decodedData.isAdmin
            if (environment.subdomain === 'master') {
              resolve(data)
              return
            }
            this.manageSessionCardinality(data.authToken).then(() => resolve(data))
          }
        },
        error: err => {
          reject(err)
        }
      })
    })
  }

  /**
   * SessionCardinal: if this user already has >= 2 active sessions (this SPA's source),
   * open the non-dismissable conflict dialog and block until the user chooses USE HERE
   * (keep) or LOGOUT HERE. Runs on every app entry — both the fresh-login getUserInfo path
   * AND the cross-SPA bridge reload path — so the dialog re-appears on reload and can't be
   * sidestepped by refreshing (TC-16). Resolves once resolved/absent so the caller proceeds.
   */
  public manageSessionCardinality(authToken: string): Promise<void> {
    return new Promise(resolve => {
      if (environment.subdomain === 'master') {
        resolve()
        return
      }
      this.api
        .sendRequest({
          apiBase: `${environment.apiBase}/api/v2/`,
          method: 'get',
          endpoint: `users/session-count/${environment.keycloakClientId}`,
          // Scope SessionCardinal to this SPA's source so a same-browser
          // agent SPA session (UI) is not counted against the admin.
          queryParams: { source: 'ADMIN_UI' }
        })
        .subscribe({
          next: count => {
            if (count >= 2) {
              const dialogRef = this.dialog.open(SessionComponent, {
                width: '600px',
                disableClose: true,
                data: { authToken }
              })
              dialogRef.afterClosed().subscribe(result => {
                if (!result) {
                  // App-sessions ON: skip identity.logout (would kill the global SSO
                  // and bounce every other signed-in browser). Force credential prompt.
                  if (environment.appSessionsEnabled) {
                    this.sessionStorage.clearAll()
                    this.identity.login({ promptCredentials: true }).catch(() => {
                      /* ignore */
                    })
                  } else {
                    // Same latch as logout(), else onAuthLogout makes LOGOUT HERE a no-op.
                    this.isSigningOut = true
                    this.identity.logout(window.location.origin + window.location.pathname)
                  }
                }
                resolve()
              })
            } else {
              resolve()
            }
          },
          error: () => resolve()
        })
    })
  }

  async logout() {
    // Latch before any await: revoke and provider logout both race the re-auth hooks.
    this.isSigningOut = true
    const finishLocalLogout = async (resolve: (v: unknown) => void, reject: (e: unknown) => void) => {
      try {
        this.sessionStorage.clearAll()
        this.storage.clearAll()
        await this.identity.logout(window.location.origin + window.location.pathname)
        resolve(true)
      } catch (err) {
        reject(err)
      }
    }
    return new Promise((resolve, reject) => {
      // Skip /users/logout when there's no app-session token — the interceptor
      // would cancel the pre-flight and fire a bogus "Session expired" toast.
      const hasAppSession = environment.appSessionsEnabled && !!this.storage.get(StorageKeys.authToken)
      if (!hasAppSession) {
        void finishLocalLogout(resolve, reject)
        return
      }
      // Best-effort: a failing /users/logout must not abort the local + provider logout and leave the user signed in.
      this.service.logout().subscribe({
        next: () => void finishLocalLogout(resolve, reject),
        error: err => {
          console.error('Error during logout API call:', err)
          void finishLocalLogout(resolve, reject)
        }
      })
    })
  }

  public setUserInfoMessage(errorType: string) {
    this.service.setValue(errorType)
    this.router.navigate(['error'])
  }

  /** Probe the kmAuth cookie for a live cross-SPA session. Returns UI-info payload or null. */
  public bridgeSignIn(): Promise<any | null> {
    if (this.isSigningOut) return Promise.resolve(null)
    // Present our own token or the backend cannot reuse and mints a new session on every load.
    const ownToken = this.storage.get(StorageKeys.authToken) as string
    return fetch(`${environment.apiBase}/api/v2/users/sso-bridge?source=ADMIN_UI`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        subdomain: environment.subdomain,
        'auth-subdomain': environment.keycloakRealm || environment.subdomain,
        ...(ownToken ? { 'auth-token': ownToken } : {})
      }
    })
      .then(async res => {
        if (!res.ok) return null
        const body = await res.json()
        return body?.result ?? body
      })
      .catch(() => null)
  }

  public getCompanyInfo() {
    try {
      return this.api.sendRequest({
        apiBase: `${environment.apiBase}/api/v2/`,
        method: 'get',
        endpoint: 'company/profile'
      })
    } catch (err) {
      console.log(err)
    }
  }

  async loginAction() {
    return new Promise((resolve, reject) => {
      const res = this.api.sendRequest({
        apiBase: `${environment.apiBase}/api/v2/`,
        method: 'get',
        endpoint: 'users/login-actions'
      })
      res.subscribe({
        next: data => {
          resolve(data)
        },
        error: err => {
          reject(err)
        }
      })
    })
  }
}
